import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { HandshakeIcon, LogOutIcon, HomeIcon, MessageSquareIcon, BellIcon, UsersIcon, LayoutDashboardIcon, Menu as MenuIcon, X as XIcon } from 'lucide-react';
import { TechBackground, TechGrid } from './TechEffects';
interface DashboardLayoutProps {
  children: React.ReactNode;
  userType: 'brand' | 'organizer' | 'admin';
}
export function DashboardLayout({
  children,
  userType
}: DashboardLayoutProps) {
  const {
    currentUser,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const navItems = {
    brand: [{
      label: 'Home',
      icon: <HomeIcon className="h-5 w-5" data-id="element-332" />,
      path: '/'
    }, {
      label: 'Dashboard',
      icon: <LayoutDashboardIcon className="h-5 w-5" data-id="element-333" />,
      path: '/dashboard/brand'
    }, {
      label: 'Matches',
      icon: <UsersIcon className="h-5 w-5" data-id="element-334" />,
      path: '/dashboard/matches'
    }, {
      label: 'Messages',
      icon: <MessageSquareIcon className="h-5 w-5" data-id="element-335" />,
      path: '/dashboard/messages'
    }],
    organizer: [{
      label: 'Home',
      icon: <HomeIcon className="h-5 w-5" data-id="element-336" />,
      path: '/'
    }, {
      label: 'Dashboard',
      icon: <LayoutDashboardIcon className="h-5 w-5" data-id="element-337" />,
      path: '/dashboard/organizer'
    }, {
      label: 'Matches',
      icon: <UsersIcon className="h-5 w-5" data-id="element-338" />,
      path: '/dashboard/matches'
    }, {
      label: 'Messages',
      icon: <MessageSquareIcon className="h-5 w-5" data-id="element-339" />,
      path: '/dashboard/messages'
    }],
    admin: [{
      label: 'Home',
      icon: <HomeIcon className="h-5 w-5" data-id="element-340" />,
      path: '/'
    }, {
      label: 'Dashboard',
      icon: <LayoutDashboardIcon className="h-5 w-5" data-id="element-341" />,
      path: '/admin'
    }, {
      label: 'Brands',
      icon: <UsersIcon className="h-5 w-5" data-id="element-342" />,
      path: '/admin/brands'
    }, {
      label: 'Organizers',
      icon: <UsersIcon className="h-5 w-5" data-id="element-343" />,
      path: '/admin/organizers'
    }, {
      label: 'Matches',
      icon: <HandshakeIcon className="h-5 w-5" data-id="element-344" />,
      path: '/admin/matches'
    }]
  };
  const currentNavItems = navItems[userType];
  return <div className="min-h-screen bg-gray-50 flex relative overflow-hidden" data-id="element-345">
      {/* Tech background */}
      <div className="absolute inset-0 z-0" data-id="element-346">
        <TechBackground data-id="element-347" />
      </div>
      {/* Sidebar */}
      <div className="bg-white bg-opacity-95 backdrop-blur-sm w-64 border-r hidden md:block relative z-10" data-id="element-348">
        <div className="h-16 flex items-center px-6 border-b" data-id="element-349">
          <Link to="/" className="flex items-center space-x-2 group" data-id="element-350">
            <div className="bg-blue-600 p-2 rounded-md relative overflow-hidden tech-pulse" data-id="element-351">
              <HandshakeIcon className="h-6 w-6 text-white relative z-10" data-id="element-352" />
            </div>
            <span className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-353">
              SponsrAI
            </span>
          </Link>
        </div>
        <div className="p-4" data-id="element-354">
          <div className="mb-6" data-id="element-355">
            <div className="text-sm font-medium text-gray-400 mb-2" data-id="element-356">MENU</div>
            <nav className="space-y-1" data-id="element-357">
              {currentNavItems.map(item => <Link key={item.path} to={item.path} className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors ${location.pathname === item.path || item.path !== '/' && location.pathname.startsWith(item.path) ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 pl-3' : ''}`} data-id="element-358">
                  {item.icon}
                  <span className="ml-3" data-id="element-359">{item.label}</span>
                </Link>)}
            </nav>
          </div>
          <div data-id="element-360">
            <div className="text-sm font-medium text-gray-400 mb-2" data-id="element-361">
              ACCOUNT
            </div>
            <button onClick={handleLogout} className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-362">
              <LogOutIcon className="h-5 w-5" data-id="element-363" />
              <span className="ml-3" data-id="element-364">Log Out</span>
            </button>
          </div>
        </div>
      </div>
      {/* Main content */}
      <div className="flex-1 flex flex-col relative z-10" data-id="element-365">
        {/* Header */}
        <header className="bg-white bg-opacity-95 backdrop-blur-sm h-16 border-b flex items-center justify-between px-6 relative z-20" data-id="element-366">
          <div className="flex items-center" data-id="element-367">
            <button className="md:hidden mr-4 text-gray-500 hover:text-gray-700" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-368">
              {mobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-369" /> : <MenuIcon className="h-6 w-6" data-id="element-370" />}
            </button>
            <span className="text-xl font-bold text-gray-900 md:hidden" data-id="element-371">
              SponsrAI
            </span>
          </div>
          <div className="flex items-center space-x-4" data-id="element-372">
            <button className="text-gray-500 hover:text-gray-700 relative" data-id="element-373">
              <BellIcon className="h-6 w-6" data-id="element-374" />
              <span className="absolute top-0 right-0 h-2 w-2 bg-blue-600 rounded-full" data-id="element-375"></span>
            </button>
            <div className="flex items-center" data-id="element-376">
              <div className="bg-blue-100 text-blue-800 rounded-full h-8 w-8 flex items-center justify-center" data-id="element-377">
                {currentUser?.name?.charAt(0) || '?'}
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-378">
                {currentUser?.name || 'User'}
              </span>
            </div>
          </div>
        </header>
        {/* Mobile menu */}
        {mobileMenuOpen && <div className="md:hidden bg-white bg-opacity-95 backdrop-blur-sm border-b relative z-10" data-id="element-379">
            <nav className="px-4 py-2" data-id="element-380">
              {currentNavItems.map(item => <Link key={item.path} to={item.path} className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md ${location.pathname === item.path || item.path !== '/' && location.pathname.startsWith(item.path) ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 pl-3' : ''}`} onClick={() => setMobileMenuOpen(false)} data-id="element-381">
                  {item.icon}
                  <span className="ml-3" data-id="element-382">{item.label}</span>
                </Link>)}
              <button onClick={() => {
            handleLogout();
            setMobileMenuOpen(false);
          }} className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md" data-id="element-383">
                <LogOutIcon className="h-5 w-5" data-id="element-384" />
                <span className="ml-3" data-id="element-385">Log Out</span>
              </button>
            </nav>
          </div>}
        {/* Content */}
        <main className="flex-1 overflow-auto p-6 relative" data-id="element-386">
          <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-387">
            {children}
          </div>
        </main>
      </div>
    </div>;
}